import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { CurrencyCode } from "@/types";

import DataTable from "@/components/molecules/DataTable";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import Table from "@/components/atoms/Table";
import Text from "@/components/atoms/Text";
import NumberFormatter from "@/components/atoms/NumberFormatter";

interface OutstandingTableProps<T extends object> {
  rows: T[];
  /** Reads the id off a row - the accounting DTOs name that field differently */
  getId: (row: T) => number;
  /** Reads the amount off a row - the accounting DTOs name that field differently */
  getAmount: (row: T) => number;
  total: number;
  currency: CurrencyCode;
  isLoading: boolean;
  isError: boolean;
}

export default function OutstandingTable<T extends object>({
  rows,
  getId,
  getAmount,
  total,
  currency,
  isLoading,
  isError,
}: OutstandingTableProps<T>) {
  const { t } = useTranslation();

  const columns: ColumnDef<T>[] = useMemo(
    () => [
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:id")} />,
        id: "id",
        cell: ({ row }) => <Text size="sm">{getId(row.original)}</Text>,
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:sum")} ta="right" />,
        id: "amount",
        cell: ({ row }) => (
          <Text ta="right" size="sm">
            <NumberFormatter
              value={getAmount(row.original)}
              decimalScale={2}
              fixedDecimalScale
              suffix={` ${currency}`}
            />
          </Text>
        ),
      },
    ],
    [t, currency, getId, getAmount]
  );

  if (isError) {
    return (
      <Text p="md" size="sm" color="secondary">
        {t("commonAdmin:retrievingFailed")}
      </Text>
    );
  }

  return (
    <DataTable
      onRowClick={undefined}
      data={rows}
      columns={columns}
      isLoading={isLoading}
      actionable={false}
      showPagination={false}
      mobileHeaderCell={0}
      rowsPerPage={Math.max(rows.length, 5)}
      footerRow={
        <Table.Tr>
          <Table.Td>
            <Text size="sm" strong>
              {t("common:total")}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text ta="right" size="sm" strong>
              <NumberFormatter value={total} decimalScale={2} fixedDecimalScale suffix={` ${currency}`} />
            </Text>
          </Table.Td>
        </Table.Tr>
      }
    />
  );
}
